import styled from '@emotion/styled';
import { Link, useMediaQuery } from '@material-ui/core';
import Grid, { GridSize } from '@material-ui/core/Grid/Grid';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useSearchParam } from 'react-use';

import { useGetOrganizationIdFromState } from '../../../../hooks/useGetOrganizationIdFromState';
import { SCREEN_LARGE } from '../../../constant/pageWidth';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import mq, { breakpoint } from '../../../styleUtility/mediaquery';
import { InsightCategories, InsightCategory } from '../../../types/InsightCategory';
import EmptyState from './EmptyState';
import { InsightCategoryListSkeleton } from './InsightCategoryListSkeleton';
import { InsightCategoryTitle } from './InsightCategoryTitle';
import LatestInsightItem from './LatestInsightItem';

type Props = {
  loading: boolean;
  showEmptyState: boolean;
  insightCategories: InsightCategories | null | undefined;
  isSingleCategoryPage: boolean;
  className?: string;
};

const LatestInsightsList: FC<Props> = ({
  loading,
  showEmptyState,
  insightCategories,
  isSingleCategoryPage,
  className,
}) => {
  const orgId = useSearchParam('org');
  const { organizationId } = useGetOrganizationIdFromState(orgId);

  const isPC = useMediaQuery(`(min-width:${breakpoint['sp']}px)`);

  const renderArticles = (category: InsightCategory) => {
    const { pcColumnCount, spColumnCount, rowCount } = category;
    const articleCount = isPC ? pcColumnCount * rowCount : spColumnCount * rowCount;
    const articles = isSingleCategoryPage ? category.articles : category.articles.slice(0, articleCount);

    return (
      <Grid container spacing={2}>
        {articles.map((article) => (
          <CardItem
            key={article.id}
            item
            md={setColumnGrid(category.pcColumnCount)}
            sm={6}
            xs={setColumnGrid(category.spColumnCount)}
          >
            <LatestInsightItem
              key={article.id}
              url={article.url}
              title={article.title || undefined}
              thumbnailImageUrl={article.thumbnailImageUrl}
              organizationId={organizationId}
            />
          </CardItem>
        ))}
      </Grid>
    );
  };

  return (
    <Root className={className}>
      {showEmptyState ? (
        <EmptyState />
      ) : (
        <>
          {loading ? (
            <InsightCategoryListSkeleton />
          ) : (
            insightCategories?.map((insightCategory) => (
              <Fragment key={insightCategory.id}>
                <InsightCategoryTitle
                  orgId={orgId}
                  insightCategory={insightCategory}
                  isSingleCategoryPage={isSingleCategoryPage}
                />
                {renderArticles(insightCategory)}

                {!isSingleCategoryPage && (
                  <CategoryLinkContainer container justify="flex-end">
                    <CategoryLink
                      underline="none"
                      component={RouterLink}
                      to={`/insight-categories/${insightCategory.id}${orgId != null ? `?org=${orgId}` : ''}`}
                    >
                      <FormattedMessage id="latest_insights_page.category_link_message" />
                    </CategoryLink>
                  </CategoryLinkContainer>
                )}
              </Fragment>
            ))
          )}
        </>
      )}
    </Root>
  );
};

const setColumnGrid = (columnCount: number): GridSize => {
  return (12 / columnCount) as GridSize;
};

const Root = styled('div')`
  width: auto;
  max-width: ${SCREEN_LARGE}px;
  margin-top: ${SPACING_HUGE}px;
  margin-right: auto;
  margin-left: auto;
  padding-bottom: ${SPACING_UNIT * 12}px;

  ${mq('sp')} {
    padding-left: ${SPACING_REGULAR}px;
    padding-right: ${SPACING_REGULAR}px;
  }
`;

const CardItem = styled(Grid)`
  ${mq('sp')} {
    min-height: 150px;
  }
`;

const CategoryLink = styled(Link)`
  font-size: 16px;
` as typeof Link;

const CategoryLinkContainer = styled(Grid)`
  margin-top: 12px;
  margin-bottom: 24px;
`;

export default LatestInsightsList;
